import {
  Box,
  ComboboxChevron,
  type ComboboxData,
  type ComboboxItem,
  Group,
  Select,
  type SelectProps
} from "@mantine/core";
import classNames from "classnames";
import type { CommonFormFieldProps } from "../FormField";
import { FormFieldLabel } from "../FormFieldLabel";
import { FormFieldRightSection } from "../FormFieldRightSection";

export type NumericComboboxItem = Omit<ComboboxItem, "value"> & {
  value: number;
};

interface FormFieldSelectProps
  extends
    Omit<CommonFormFieldProps, "error">,
    Omit<SelectProps, "data" | "value" | "defaultValue" | "onChange"> {
  type: "select";
  action?: React.ReactNode;
  data?: ComboboxData | Array<NumericComboboxItem>;
  defaultValue?: string | number | null;
  value?: string | number | null;
  onChange?: (value: string | number | null) => void;
}

/* This component can handle string and number values. */
function FormFieldSelect({
  action,
  helpText,
  highlight,
  isBetaFeature,
  type,
  warned,
  data,
  value,
  defaultValue,
  onChange,
  ...fieldSpecificProps
}: FormFieldSelectProps) {
  const hasNumberValues =
    Array.isArray(data) &&
    typeof data[0] === "object" &&
    "value" in data[0] &&
    typeof data[0].value === "number";

  const parsedData: SelectProps["data"] = hasNumberValues
    ? (data as Array<{ value: number; label: string }>).map((item) => ({
        value: item.value.toString(),
        label: item.label
      }))
    : (data as ComboboxData);
  const parsedValue = value === null ? null : value?.toString();
  const parsedDefaultValue = defaultValue?.toString();

  function handleChangeWithParsing(newValue: string | null) {
    if (hasNumberValues) {
      const numericValue = newValue === null ? null : Number(newValue);
      onChange?.(numericValue);
    } else {
      onChange?.(newValue);
    }
  }

  const manualRightSectionWidth = warned
    ? fieldSpecificProps.clearable
      ? 76
      : 60
    : undefined;

  return (
    <Select
      {...fieldSpecificProps}
      aria-label={
        typeof fieldSpecificProps.label === "string"
          ? fieldSpecificProps.label
          : undefined
      }
      className={classNames("FormFieldSelect", fieldSpecificProps.className)}
      data={parsedData}
      defaultValue={parsedDefaultValue}
      inputContainer={
        action
          ? (children) => (
              <Group align="center" gap="xs" wrap="nowrap">
                <Box flex={1} miw={0}>
                  {children}
                </Box>
                {action}
              </Group>
            )
          : undefined
      }
      label={
        fieldSpecificProps.label && (
          <FormFieldLabel
            helpText={helpText}
            highlight={highlight}
            isBetaFeature={isBetaFeature}
            label={fieldSpecificProps.label}
            required={fieldSpecificProps.required}
          />
        )
      }
      labelProps={{ w: "100%", ...fieldSpecificProps.labelProps }}
      nothingFoundMessage={
        fieldSpecificProps.nothingFoundMessage || "Keine Ergebnisse gefunden"
      }
      rightSection={
        (warned || fieldSpecificProps.rightSection) && (
          <FormFieldRightSection
            rightSection={
              <>
                {fieldSpecificProps.rightSection}
                <ComboboxChevron />
              </>
            }
            warned={warned}
          />
        )
      }
      styles={{
        section: {
          width: fieldSpecificProps.rightSectionWidth || manualRightSectionWidth
        }
      }}
      value={parsedValue}
      onChange={handleChangeWithParsing}
    />
  );
}

export { FormFieldSelect, FormFieldSelectProps };
